import type { Building } from "building-types/building/buildingTypes";
import type { InteriorPalette } from "building-types/materials/interiorPaletteTypes";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import { optionSetSelectedOptionId } from "doc/options/optionSetTools";
import { interiorPaletteOptionSetId } from "doc/options/settingsOptionSet";
import interiorPaletteLibrary, { defaultInteriorPalette } from "doc/settings/interiorMaterialSettingsLibrary";
import memo from "../utils/memo";

function select({ doc, optionChoices, settings }: Building) {
  const interiorMaterialSettings = settings.interiorMaterials;
  const settingsId = interiorMaterialSettings.id;

  return {
    interiorMaterialSettings: settings.interiorMaterials,
    palettes: doc.palettes,
    interiorPaletteId: optionSetSelectedOptionId(
      docToOptionDefinitions(doc),
      optionChoices,
      interiorPaletteOptionSetId(settingsId),
    ),
  };
}

function generate(props: ReturnType<typeof select>) {
  const { interiorMaterialSettings } = props;
  const interiorPalette = interiorPaletteFromSettings(props) ?? defaultInteriorPalette({ sortIndex: 0 });
  let modifiedInteriorPalette = { ...interiorPalette };
  //load palette slot user defined names from settings
  if (interiorPalette) {
    const id = interiorPalette.id;
    const paletteSlotUserDefinedNames = interiorMaterialSettings.paletteSlotsUserDefinedNames?.[id];
    if (paletteSlotUserDefinedNames) {
      for (const key in paletteSlotUserDefinedNames) {
        const userDefinedNames = paletteSlotUserDefinedNames[key];
        if (key === "defaultRoomMaterials") {
          //copy default room materials
          let defaultRoomMaterials = { ...modifiedInteriorPalette.defaultRoomMaterials };
          for (const key2 in userDefinedNames) {
            defaultRoomMaterials = {
              ...defaultRoomMaterials,
              [key2]: { userDefinedName: userDefinedNames[key2] },
            };
          }
          //update modifiedInteriorPalette
          modifiedInteriorPalette = { ...modifiedInteriorPalette, defaultRoomMaterials };
        } else {
          //copy room materials
          const roomMaterialsArray = [...modifiedInteriorPalette.roomMaterials];
          for (const key2 in userDefinedNames) {
            roomMaterialsArray[+key2] = {
              ...roomMaterialsArray[+key2],
              [key2]: { userDefinedName: userDefinedNames[key2] },
            };
          }
          //update modifiedInteriorPalette
          modifiedInteriorPalette = { ...modifiedInteriorPalette, roomMaterials: roomMaterialsArray };
        }
      }
    }
  }
  return { interiorPalette: modifiedInteriorPalette };
}

const interiorPaletteFromSettings = ({
  interiorPaletteId,
  interiorMaterialSettings,
  palettes,
}: ReturnType<typeof select>): InteriorPalette => {
  if (interiorPaletteId === null) {
    const interiorPaletteId = interiorMaterialSettings.palettes[0];
    return interiorPaletteLibrary[interiorPaletteId] ?? (palettes[interiorPaletteId] as InteriorPalette);
  }

  return interiorPaletteLibrary[interiorPaletteId] ?? (palettes[interiorPaletteId] as InteriorPalette);
};

export default memo(select, generate);
